/**
 * TimeRangeSelector V3.3
 * Selector visual de rangos de tiempo semanales
 * 
 * @author Claude
 * @version 3.3.0
 * @license MIT
 * 
 * Características V3.3:
 * - Sistema de selección (click para activar)
 * - Fix responsive (ancho mínimo + scroll horizontal)
 * - ⭐ NUEVO: Alt + Drag para copiar bloques
 * - Crear, mover, redimensionar, eliminar bloques
 * - Modo responsive inteligente
 * - Validación de colisiones con gap
 * - Snap automático a grid
 * - Dos modos: normal y prohibir
 * - Modo disabled
 * 
 * Cambios V3.3:
 * - Añadido: Alt + Drag para copiar bloques
 * - Añadido: Preview visual de copia con línea punteada
 * - Añadido: Conversión dinámica entre mover y copiar
 */

(function (window) {
  'use strict';

  class TimeRangeSelector {
    /**
     * Constructor
     * @param {HTMLElement|string} container - Elemento DOM o selector
     * @param {Object} options - Opciones de configuración
     */
    constructor(container, options = {}) {
      this.container = typeof container === 'string'
        ? document.getElementById(container)
        : container;

      if (!this.container) {
        throw new Error('Container element not found');
      }

      // Configuración por defecto
      this.options = {
        cellWidth: 8,
        cellHeight: 40,
        minutesPerCell: 5,
        startHour: 0,
        endHour: 24,
        gapMinutes: 5,
        snapMinutes: 5,
        responsive: true,
        prohibir: false,
        singleSelect: false,
        days: ['lunes', 'martes', 'miercoles', 'jueves', 'viernes', 'sabado', 'domingo'],
        dayLabels: ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'],
        colors: {
          default: '#f0f0f0',
          disabledSelected: '#8f97c28b',
          disabledProhibir: '#ae898c90',
          selected: '#294adb',
          prohibir: '#dc3545',
          hover: '#5a7dd9',
          hoverProhibir: '#c9302c',
          border: '#ffffff',
          gridLine: '#e0e0e0',
          gridLineMajor: '#bdbdbd',
          blockBorder: '#ffffff',
          blockBorderHover: '#333333',
          selectedBorder: '#FFD700',
          copyBorder: '#00FF00'  // ⭐ NUEVO: Color para preview de copia
        },
        mode: 'normal',
        onChange: null,
        allowDebugging: false,
        ...options
      };

      this.days = this.options.days;
      this.prohibir = this.options.prohibir;
      this.dayLabels = this.options.dayLabels;
      this.currentMode = this.options.mode;
      this.allowDebugging = this.options.allowDebugging;
      this.singleSelect = this.options.singleSelect;

      // Estado
      this.blocks = [];
      this.disabled = false;
      this.nextBlockId = 1;
      this.selectedBlockId = null;
      this.activeBlock = null;
      this.dragState = null;
      this.resizeState = null;
      this.isCreating = false;
      this.createStart = null;
      this.lockedDay = null;
      this.svg = null;
      this.blocksGroup = null;
      this.gridDimensions = null;
      this.minX = 0;
      this.minY = 0;
      
      // ⭐ NUEVO: Estado de tecla Alt para copiar
      this.isAltPressed = false;

      this.init();
    }

    /**
     * Inicializa el selector
     */
    init() {
      this.createSVG();
      this.createGrid();
      this.attachEvents();
      this.attachKeyboardEvents();  // ⭐ NUEVO
    }

    /**
     * Imprime en consola
     * @param {string} text Debug a mostrar
     */
    printDebug(text, ...params) {
      if (this.allowDebugging) {
        console.log(text, ...params);
      }
    }

    /**
     * Indica si un bloque prohibir debe comportarse como bloque bloqueado
     */
    isProhibirLocked(block) {
      return Boolean(this.options.prohibir && block && block.prohibir);
    }

    /**
     * ⭐ NUEVO: Adjunta eventos de teclado para detectar Alt
     */
    attachKeyboardEvents() {
      // Detectar cuando se presiona Alt
      this.handleKeyDown = (e) => {
        if (e.key === 'Alt' || e.altKey) {
          this.isAltPressed = true;
          
          // Cambiar cursor si hay un bloque seleccionado
          if (this.selectedBlockId !== null) {
            this.svg.style.cursor = 'copy';
          }
          
          this.printDebug('[KEYBOARD] Alt presionado - Modo COPIA activado');
          
          // ⭐ Si ya está en drag normal, convertir a drag de copia
          if (this.dragState && !this.dragState.isCopying) {
            this.convertToCopyMode();
          }
        }
      };

      // Detectar cuando se suelta Alt
      this.handleKeyUp = (e) => {
        if (e.key === 'Alt' || !e.altKey) {
          this.isAltPressed = false;
          
          // Restaurar cursor
          if (this.selectedBlockId !== null) {
            this.svg.style.cursor = 'move';
          } else {
            this.svg.style.cursor = 'default';
          }
          
          this.printDebug('[KEYBOARD] Alt soltado - Modo MOVER');
          
          // ⭐ Si está en drag de copia, convertir a drag normal
          if (this.dragState && this.dragState.isCopying) {
            this.convertToMoveMode();
          }
        }
      };

      window.addEventListener('keydown', this.handleKeyDown);
      window.addEventListener('keyup', this.handleKeyUp);
    }

    /**
     * Crea el contenedor SVG
     */
    createSVG() {
      const { minutesPerCell, startHour, endHour, responsive } = this.options;
      const totalMinutes = (endHour - startHour) * 60;
      const cols = totalMinutes / minutesPerCell;
      const rows = this.days.length;

      const containerWidth = this.container.clientWidth || 1200;

      let leftMargin = 50;
      let topMargin = 30;
      let cellHeight = this.options.cellHeight;
      let cellWidth = this.options.cellWidth;

      // Definir anchos mínimos para evitar grid ilegible
      const minGridWidth = 800;
      const minCellWidth = 4;

      if (responsive) {
        if (containerWidth < 600) {
          leftMargin = 35;
          topMargin = 25;
          cellHeight = Math.max(25, Math.min(40, containerWidth / 15));
        }

        const availableWidth = containerWidth - leftMargin - 10;
        cellWidth = Math.floor(availableWidth / cols);

        if (cellWidth < minCellWidth) {
          cellWidth = minCellWidth;
        }

        const calculatedGridWidth = leftMargin + (cols * cellWidth);
        if (calculatedGridWidth < minGridWidth && containerWidth >= 600) {
          cellWidth = Math.floor((minGridWidth - leftMargin) / cols);
        }

        this.options.cellWidth = cellWidth;
        this.options.cellHeight = cellHeight;
      }

      const width = leftMargin + (cols * cellWidth);
      const height = topMargin + (rows * cellHeight);

      this.svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');

      if (width > containerWidth) {
        this.svg.setAttribute('width', width);
      } else {
        this.svg.setAttribute('width', '100%');
      }

      this.svg.setAttribute('height', height);
      this.svg.setAttribute('viewBox', `${this.minX} ${this.minY} ${width} ${height}`);
      this.svg.setAttribute('preserveAspectRatio', 'xMinYMin meet');
      this.svg.style.userSelect = 'none';

      this.container.appendChild(this.svg);
      this.gridDimensions = { leftMargin, topMargin, cols, rows, width, height, containerWidth };

      this.blocksGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
      this.blocksGroup.setAttribute('id', 'blocks-group');
      this.svg.appendChild(this.blocksGroup);
    }

    setDisabled(disabled) {
      this.disabled = disabled;

      if (disabled) {
        this.svg.style.pointerEvents = 'none';
      } else {
        this.svg.style.pointerEvents = 'all';
      }

      this.recreateGrid();
    }

    /**
     * Crea el grid de fondo
     */
    createGrid() {
      const { cellWidth, cellHeight, minutesPerCell, startHour, endHour, colors } = this.options;
      const { leftMargin, topMargin, cols, rows, containerWidth } = this.gridDimensions;

      const isSmall = containerWidth < 600;
      const isTiny = containerWidth < 400;

      // Celdas
      for (let row = 0; row < rows; row++) {
        for (let col = 0; col < cols; col++) {
          const x = leftMargin + (col * cellWidth);
          const y = topMargin + (row * cellHeight);
          const cell = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
          cell.setAttribute('x', x);
          cell.setAttribute('y', y);
          cell.setAttribute('width', cellWidth);
          cell.setAttribute('height', cellHeight);
          cell.setAttribute('fill', colors.default);
          cell.setAttribute('stroke', colors.border);
          cell.setAttribute('stroke-width', '0.5');
          cell.setAttribute('data-day', row);
          cell.setAttribute('data-col', col);
          cell.classList.add('grid-cell');
          cell.style.pointerEvents = 'all';
          cell.style.cursor = 'crosshair';
          this.svg.insertBefore(cell, this.blocksGroup);
        }
      }

      // Etiquetas de horas
      let hourStep = 2;
      if (isTiny) hourStep = 6;
      else if (isSmall) hourStep = 4;

      const fontSize = isTiny ? '10' : isSmall ? '11' : '12';

      for (let h = startHour; h <= endHour; h += hourStep) {
        let x = leftMargin + ((h - startHour) * 60 / minutesPerCell) * cellWidth;
        let textX = x;

        if (h === 0) textX += 5;
        if (h === endHour) textX -= 5;

        this.createText(textX, topMargin - 10, h.toString().padStart(2, '0'), 'hour-label', 'middle', fontSize);

        if (h < endHour) {
          this.createLine(x, topMargin, x, topMargin + (rows * cellHeight), colors.gridLineMajor, 1.5);
        }
      }

      // Etiquetas de días
      const dayLabelsFontSize = isTiny ? '9' : isSmall ? '10' : '12';
      const labelsToUse = isTiny ? this.dayLabels.map(d => d.substring(0, 2)) : this.dayLabels;

      for (let row = 0; row < rows; row++) {
        let x = leftMargin - (isTiny ? 5 : 10);
        let y = topMargin + (row * cellHeight);

        this.createText(x, y + cellHeight / 2 + 4, labelsToUse[row], 'day-label', 'end', dayLabelsFontSize);
        this.createLine(leftMargin, y, leftMargin + (cols * cellWidth), y, colors.gridLine, 1);
      }

      this.createLine(leftMargin, topMargin + (rows * cellHeight), leftMargin + (cols * cellWidth), topMargin + (rows * cellHeight), colors.gridLine, 1);
    }

    /**
     * Crea una línea SVG
     */
    createLine(x1, y1, x2, y2, color, strokeWidth) {
      const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
      line.setAttribute('x1', x1);
      line.setAttribute('y1', y1);
      line.setAttribute('x2', x2);
      line.setAttribute('y2', y2);
      line.setAttribute('stroke', color);
      line.setAttribute('stroke-width', strokeWidth);
      line.style.pointerEvents = 'none';
      this.svg.insertBefore(line, this.blocksGroup);
      return line;
    }

    /**
     * Crea un texto SVG
     */
    createText(x, y, content, className, anchor = 'middle', fontSize = '12') {
      const text = document.createElementNS('http://www.w3.org/2000/svg', 'text');
      text.setAttribute('x', x);
      text.setAttribute('y', y);
      text.setAttribute('class', className);
      text.setAttribute('text-anchor', anchor);
      text.setAttribute('font-size', fontSize);
      text.setAttribute('fill', '#666');
      text.textContent = content;
      text.style.pointerEvents = 'none';
      this.svg.insertBefore(text, this.blocksGroup);
      return text;
    }

    /**
     * Adjunta eventos del mouse
     */
    attachEvents() {
      this.svg.addEventListener('mousedown', (e) => this.handleMouseDown(e));
      this.svg.addEventListener('mousemove', (e) => this.handleMouseMove(e));
      document.addEventListener('mouseup', (e) => this.handleMouseUp(e));
      this.svg.addEventListener('contextmenu', (e) => e.preventDefault());
    }

    /**
     * Maneja mousedown con sistema de selección
     */
    handleMouseDown(e) {
      if (this.disabled) return;
      if (e.button !== 0) return;
      e.preventDefault();

      const target = e.target;

      // Resize handle - solo si está seleccionado
      if (target.classList.contains('resize-handle')) {
        const blockElement = target.closest('.time-block');
        if (blockElement) {
          const blockId = parseInt(blockElement.getAttribute('data-block-id'));
          if (this.selectedBlockId === blockId) {
            const point = this.getMousePosition(e);
            this.startResize(target, point);
            return;
          }
        }
        return;
      }

      // Botón eliminar - solo si está seleccionado
      if (target.classList.contains('delete-button')) {
        const blockElement = target.closest('.time-block');
        if (blockElement) {
          const blockId = parseInt(blockElement.getAttribute('data-block-id'));
          if (this.selectedBlockId === blockId) {
            e.stopPropagation();
            this.deleteBlock(blockId);
            return;
          }
        }
        return;
      }

      // Click en bloque
      const blockElement = target.closest('.time-block');
      if (blockElement) {
        const blockId = parseInt(blockElement.getAttribute('data-block-id'));
        const block = this.blocks.find(b => b.id === blockId);

        // Primera vez: seleccionar
        if (this.selectedBlockId !== blockId) {
          this.selectBlock(blockId);
          return;
        }

        // Segunda vez: permitir mover (o copiar si Alt está presionado)
        if (this.selectedBlockId === blockId) {
          // Los bloques prohibir quedan fijos: se pueden seleccionar pero no mover/copiar
          if (this.isProhibirLocked(block)) {
            return;
          }

          const point = this.getMousePosition(e);
          this.startDrag(blockId, point);
          return;
        }
      }

      // Click fuera: deseleccionar
      if (this.selectedBlockId !== null) {
        this.deselectBlock();
      }

      // Click en celda: crear nuevo bloque
      if (target.classList.contains('grid-cell')) {
        const day = parseInt(target.getAttribute('data-day'));
        const col = parseInt(target.getAttribute('data-col'));
        this.startCreation({ day, col });
        return;
      }

      const point = this.getMousePosition(e);
      const cell = this.getCellFromPoint(point);
      if (cell) {
        this.startCreation(cell);
      }
    }

    /**
     * Selecciona un bloque
     */
    selectBlock(blockId) {
      this.printDebug('[SELECT] Bloque seleccionado:', blockId);
      this.selectedBlockId = blockId;
      this.blocks.forEach(b => b.selected = b.id === blockId);
      this.renderBlocks();
    }

    /**
     * Deselecciona el bloque actual
     */
    deselectBlock() {
      this.printDebug('[DESELECT] Bloque deseleccionado');
      this.selectedBlockId = null;
      this.blocks.forEach(b => b.selected = false);
      this.renderBlocks();
    }

    /**
     * Maneja mousemove
     */
    handleMouseMove(e) {
      if (this.disabled) return;

      const point = this.getMousePosition(e);

      if (this.resizeState) {
        this.updateResize(point);
      } else if (this.dragState) {
        this.updateDrag(point);
      } else if (this.isCreating) {
        this.updateCreation(point);
      } else {
        this.updateCursor(e.target);
      }
    }

    /**
     * Maneja mouseup
     */
    handleMouseUp(e) {
      if (this.disabled) return;
      if (e.button !== 0) return;

      if (this.resizeState) {
        this.finishResize();
      } else if (this.dragState) {
        this.finishDrag();
      } else if (this.isCreating) {
        this.finishCreation();
      }
    }

    /**
     * Inicia creación de bloque
     */
    startCreation(cell) {
      this.lockedDay = cell.day;
      this.isCreating = true;
      this.createStart = { day: cell.day, col: cell.col };
    }

    /**
     * Actualiza creación durante arrastre
     */
    updateCreation(point) {
      if (!this.isCreating || !this.createStart) return;

      const cell = this.getCellFromPoint(point);
      if (!cell || cell.day !== this.lockedDay) return;

      const startCol = Math.min(this.createStart.col, cell.col);
      const endCol = Math.max(this.createStart.col, cell.col) + 1;

      this.showCreationPreview(this.createStart.day, startCol, endCol);
    }

    /**
     * Finaliza creación
     */
    finishCreation() {
      if (!this.isCreating || !this.createStart) return;

      const preview = this.svg.querySelector('.creation-preview');
      if (preview) {
        const day = parseInt(preview.getAttribute('data-day'));
        const startCol = parseInt(preview.getAttribute('data-start'));
        const endCol = parseInt(preview.getAttribute('data-end'));

        if (endCol - startCol >= 1) {
          const snappedStart = this.snapToGrid(startCol);
          const snappedEnd = this.snapToGrid(endCol);

          if (!this.hasCollision(day, snappedStart, snappedEnd)) {
            this.addBlock(day, snappedStart, snappedEnd, this.currentMode === 'prohibir');
          }
        }

        preview.remove();
      }

      this.isCreating = false;
      this.createStart = null;
      this.lockedDay = null;
    }

    /**
     * Muestra preview durante creación
     */
    showCreationPreview(day, startCol, endCol) {
      let preview = this.svg.querySelector('.creation-preview');

      const { cellWidth, cellHeight, colors } = this.options;
      const { leftMargin, topMargin, containerWidth } = this.gridDimensions;

      const x = leftMargin + (startCol * cellWidth);
      const y = topMargin + (day * cellHeight);
      const width = (endCol - startCol) * cellWidth;
      const height = cellHeight - 4;

      const color = this.currentMode === 'prohibir' ? colors.prohibir : colors.selected;

      if (!preview) {
        preview = document.createElementNS('http://www.w3.org/2000/svg', 'g');
        preview.classList.add('creation-preview');
        preview.style.opacity = '0.6';
        preview.style.pointerEvents = 'none';

        const rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
        rect.setAttribute('x', x);
        rect.setAttribute('y', y + 2);
        rect.setAttribute('width', width);
        rect.setAttribute('height', height);
        rect.setAttribute('fill', color);
        rect.setAttribute('stroke', colors.blockBorder);
        rect.setAttribute('stroke-width', '2');
        rect.setAttribute('rx', '4');
        preview.appendChild(rect);

        const showText = width > 30;
        if (showText) {
          const isTiny = containerWidth < 400;
          const isSmall = containerWidth < 600;
          const textFontSize = isTiny ? '8' : isSmall ? '9' : '11';

          const timeText = this.getTimeFromColumn(startCol) + ' - ' + this.getTimeFromColumn(endCol);
          const text = document.createElementNS('http://www.w3.org/2000/svg', 'text');
          text.setAttribute('x', x + width / 2);
          text.setAttribute('y', y + height / 2 + 4);
          text.setAttribute('text-anchor', 'middle');
          text.setAttribute('font-size', textFontSize);
          text.setAttribute('font-weight', 'bold');
          text.setAttribute('fill', 'white');
          text.style.pointerEvents = 'none';
          text.textContent = timeText;
          preview.appendChild(text);
        }

        preview.setAttribute('data-day', day);
        preview.setAttribute('data-start', startCol);
        preview.setAttribute('data-end', endCol);

        this.blocksGroup.appendChild(preview);
      } else {
        const rect = preview.querySelector('rect');
        rect.setAttribute('x', x);
        rect.setAttribute('y', y + 2);
        rect.setAttribute('width', width);

        const existingText = preview.querySelector('text');
        const showText = width > 30;

        if (showText) {
          const isTiny = containerWidth < 400;
          const isSmall = containerWidth < 600;
          const textFontSize = isTiny ? '8' : isSmall ? '9' : '11';
          const timeText = this.getTimeFromColumn(startCol) + ' - ' + this.getTimeFromColumn(endCol);

          if (existingText) {
            existingText.textContent = timeText;
            existingText.setAttribute('x', x + width / 2);
            existingText.setAttribute('y', y + height / 2 + 4);
            existingText.setAttribute('font-size', textFontSize);
          } else {
            const text = document.createElementNS('http://www.w3.org/2000/svg', 'text');
            text.setAttribute('x', x + width / 2);
            text.setAttribute('y', y + height / 2 + 4);
            text.setAttribute('text-anchor', 'middle');
            text.setAttribute('font-size', textFontSize);
            text.setAttribute('font-weight', 'bold');
            text.setAttribute('fill', 'white');
            text.style.pointerEvents = 'none';
            text.textContent = timeText;
            preview.appendChild(text);
          }
        } else if (existingText) {
          existingText.remove();
        }

        preview.setAttribute('data-day', day);
        preview.setAttribute('data-start', startCol);
        preview.setAttribute('data-end', endCol);
      }
    }

    /**
     * Inicia arrastre de bloque
     */
    startDrag(blockId, point) {
      const block = this.blocks.find(b => b.id === blockId);
      if (!block) return;
      if (this.isProhibirLocked(block)) return;

      this.dragState = {
        blockId: blockId,
        startPoint: point,
        originalStartCol: block.startCol,
        originalEndCol: block.endCol,
        originalDay: block.dia,
        isCopying: this.isAltPressed  // ⭐ NUEVO: guardar si está en modo copia
      };

      this.activeBlock = blockId;
      
      // ⭐ NUEVO: Si está copiando, crear preview
      if (this.isAltPressed) {
        this.printDebug('[COPY] Iniciando copia de bloque', blockId);
        this.createCopyPreview(block);
      }
      
      this.updateBlockElement(block);
    }

    /**
     * ⭐ NUEVO: Crea preview visual de la copia
     */
    createCopyPreview(originalBlock) {
      const { cellWidth, cellHeight, colors } = this.options;
      const { leftMargin, topMargin, containerWidth } = this.gridDimensions;
      
      const x = leftMargin + (originalBlock.startCol * cellWidth);
      const y = topMargin + (originalBlock.dia * cellHeight);
      const width = (originalBlock.endCol - originalBlock.startCol) * cellWidth;
      const height = cellHeight - 4;
      
      const color = originalBlock.prohibir ? colors.prohibir : colors.selected;
      
      // Crear grupo para el preview
      const preview = document.createElementNS('http://www.w3.org/2000/svg', 'g');
      preview.classList.add('copy-preview');
      preview.style.opacity = '0.7';
      preview.style.pointerEvents = 'none';
      
      // Rectángulo del preview con borde punteado
      const rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
      rect.setAttribute('x', x);
      rect.setAttribute('y', y + 2);
      rect.setAttribute('width', width);
      rect.setAttribute('height', height);
      rect.setAttribute('fill', color);
      rect.setAttribute('stroke', colors.copyBorder);  // Verde para indicar copia
      rect.setAttribute('stroke-width', '3');
      rect.setAttribute('stroke-dasharray', '5,5');  // ⭐ Línea punteada
      rect.setAttribute('rx', '4');
      preview.appendChild(rect);
      
      // Badge "COPIA"
      const badgeWidth = 45;
      const badgeHeight = 16;
      const badge = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
      badge.setAttribute('x', x + 5);
      badge.setAttribute('y', y + 7);
      badge.setAttribute('width', badgeWidth);
      badge.setAttribute('height', badgeHeight);
      badge.setAttribute('fill', colors.copyBorder);
      badge.setAttribute('rx', '3');
      preview.appendChild(badge);
      
      const badgeText = document.createElementNS('http://www.w3.org/2000/svg', 'text');
      badgeText.setAttribute('x', x + 5 + badgeWidth / 2);
      badgeText.setAttribute('y', y + 7 + badgeHeight / 2 + 4);
      badgeText.setAttribute('text-anchor', 'middle');
      badgeText.setAttribute('font-size', '10');
      badgeText.setAttribute('font-weight', 'bold');
      badgeText.setAttribute('fill', '#000');
      badgeText.textContent = 'COPIA';
      badgeText.style.pointerEvents = 'none';
      preview.appendChild(badgeText);
      
      // Tiempo
      const showText = width > 30;
      if (showText) {
        const isTiny = containerWidth < 400;
        const isSmall = containerWidth < 600;
        const textFontSize = isTiny ? '8' : isSmall ? '9' : '11';
        
        const timeText = this.getTimeFromColumn(originalBlock.startCol) + ' - ' + 
                        this.getTimeFromColumn(originalBlock.endCol);
        const text = document.createElementNS('http://www.w3.org/2000/svg', 'text');
        text.setAttribute('x', x + width / 2);
        text.setAttribute('y', y + height / 2 + 12);
        text.setAttribute('text-anchor', 'middle');
        text.setAttribute('font-size', textFontSize);
        text.setAttribute('font-weight', 'bold');
        text.setAttribute('fill', 'white');
        text.style.pointerEvents = 'none';
        text.textContent = timeText;
        preview.appendChild(text);
      }
      
      // Guardar posición inicial
      preview.setAttribute('data-day', originalBlock.dia);
      preview.setAttribute('data-start', originalBlock.startCol);
      preview.setAttribute('data-end', originalBlock.endCol);
      
      this.blocksGroup.appendChild(preview);
    }

    /**
     * Actualiza arrastre
     */
    updateDrag(point) {
      if (!this.dragState) return;

      const { cellWidth, cellHeight } = this.options;
      const deltaX = point.x - this.dragState.startPoint.x;
      const deltaY = point.y - this.dragState.startPoint.y;

      const colOffset = Math.round(deltaX / cellWidth);
      const dayOffset = Math.round(deltaY / cellHeight);

      const blockWidth = this.dragState.originalEndCol - this.dragState.originalStartCol;
      let newStartCol = this.dragState.originalStartCol + colOffset;
      let newEndCol = newStartCol + blockWidth;
      let newDay = this.dragState.originalDay + dayOffset;

      newDay = Math.max(0, Math.min(this.days.length - 1, newDay));
      newStartCol = Math.max(0, newStartCol);
      newEndCol = Math.min(this.gridDimensions.cols, newEndCol);

      newStartCol = this.snapToGrid(newStartCol);
      newEndCol = this.snapToGrid(newEndCol);

      // ⭐ NUEVO: Bifurcación según modo
      if (this.dragState.isCopying) {
        // MODO COPIA: Actualizar preview, NO el bloque original
        if (!this.hasCollision(newDay, newStartCol, newEndCol, this.dragState.blockId)) {
          this.updateCopyPreview(newDay, newStartCol, newEndCol);
        }
      } else {
        // MODO NORMAL: Mover el bloque original
        const block = this.blocks.find(b => b.id === this.dragState.blockId);
        if (!block) return;

        if (!this.hasCollision(newDay, newStartCol, newEndCol, this.dragState.blockId)) {
          block.dia = newDay;
          block.startCol = newStartCol;
          block.endCol = newEndCol;
          this.updateBlockElement(block);
        }
      }
    }

    /**
     * ⭐ NUEVO: Actualiza posición del preview de copia
     */
    updateCopyPreview(day, startCol, endCol) {
      const preview = this.svg.querySelector('.copy-preview');
      if (!preview) return;

      const { cellWidth, cellHeight } = this.options;
      const { leftMargin, topMargin, containerWidth } = this.gridDimensions;
      
      const x = leftMargin + (startCol * cellWidth);
      const y = topMargin + (day * cellHeight);
      const width = (endCol - startCol) * cellWidth;
      const height = cellHeight - 4;
      
      // Actualizar rectángulo
      const rect = preview.querySelector('rect[stroke-dasharray]');
      if (rect) {
        rect.setAttribute('x', x);
        rect.setAttribute('y', y + 2);
        rect.setAttribute('width', width);
      }
      
      // Actualizar badge
      const badge = preview.querySelectorAll('rect')[1];
      if (badge) {
        badge.setAttribute('x', x + 5);
        badge.setAttribute('y', y + 7);
      }
      
      const badgeText = preview.querySelectorAll('text')[0];
      if (badgeText) {
        badgeText.setAttribute('x', x + 5 + 45 / 2);
        badgeText.setAttribute('y', y + 7 + 16 / 2 + 4);
      }
      
      // Actualizar texto de tiempo
      const texts = preview.querySelectorAll('text');
      const timeText = texts[texts.length - 1];
      if (timeText && width > 30) {
        const newTimeText = this.getTimeFromColumn(startCol) + ' - ' + this.getTimeFromColumn(endCol);
        timeText.textContent = newTimeText;
        timeText.setAttribute('x', x + width / 2);
        timeText.setAttribute('y', y + height / 2 + 12);
      }
      
      // Guardar posición para finishDrag
      preview.setAttribute('data-day', day);
      preview.setAttribute('data-start', startCol);
      preview.setAttribute('data-end', endCol);
    }

    /**
     * Finaliza arrastre
     */
    finishDrag() {
      if (!this.dragState) return;

      const originalBlock = this.blocks.find(b => b.id === this.dragState.blockId);
      
      // ⭐ NUEVO: Verificar si estaba en modo copia
      if (this.dragState.isCopying) {
        this.printDebug('[COPY] Finalizando copia');
        
        // 1. Restaurar bloque original a su posición
        if (originalBlock) {
          originalBlock.dia = this.dragState.originalDay;
          originalBlock.startCol = this.dragState.originalStartCol;
          originalBlock.endCol = this.dragState.originalEndCol;
        }
        
        // 2. Obtener posición del preview
        const preview = this.svg.querySelector('.copy-preview');
        if (preview) {
          const newDay = parseInt(preview.getAttribute('data-day'));
          const newStartCol = parseInt(preview.getAttribute('data-start'));
          const newEndCol = parseInt(preview.getAttribute('data-end'));
          
          // 3. Verificar colisión y crear copia
          if (!this.hasCollision(newDay, newStartCol, newEndCol)) {
            this.addBlock(newDay, newStartCol, newEndCol, originalBlock.prohibir);
            this.printDebug('[COPY] Copia creada exitosamente');
          } else {
            this.printDebug('[COPY] No se puede copiar: colisión detectada');
          }
          
          // 4. Remover preview
          preview.remove();
        }
        
      } else {
        // MODO NORMAL: El bloque ya se movió en updateDrag
        this.printDebug('[DRAG] Bloque movido');
      }

      this.dragState = null;
      this.activeBlock = null;
      this.notifyChange();
      this.renderBlocks();
    }

    /**
     * ⭐ NUEVO: Convierte drag normal a drag de copia (cuando se presiona Alt durante drag)
     */
    convertToCopyMode() {
      if (!this.dragState || this.dragState.isCopying) return;
      
      this.printDebug('[MOVE→COPY] Convirtiendo movimiento a copia');
      
      const block = this.blocks.find(b => b.id === this.dragState.blockId);
      if (!block) return;
      
      // Restaurar bloque a posición original
      block.dia = this.dragState.originalDay;
      block.startCol = this.dragState.originalStartCol;
      block.endCol = this.dragState.originalEndCol;
      this.updateBlockElement(block);
      
      // Crear preview de copia
      this.createCopyPreview(block);
      
      // Cambiar a modo copia
      this.dragState.isCopying = true;
      this.svg.style.cursor = 'copy';
    }

    /**
     * ⭐ NUEVO: Convierte drag de copia a drag normal (cuando se suelta Alt durante drag)
     */
    convertToMoveMode() {
      if (!this.dragState || !this.dragState.isCopying) return;
      
      this.printDebug('[COPY→MOVE] Convirtiendo copia a movimiento');
      
      // Obtener posición actual del preview
      const preview = this.svg.querySelector('.copy-preview');
      if (preview) {
        const newDay = parseInt(preview.getAttribute('data-day'));
        const newStartCol = parseInt(preview.getAttribute('data-start'));
        const newEndCol = parseInt(preview.getAttribute('data-end'));
        
        // Mover el bloque original a esa posición
        const block = this.blocks.find(b => b.id === this.dragState.blockId);
        if (block) {
          block.dia = newDay;
          block.startCol = newStartCol;
          block.endCol = newEndCol;
          this.updateBlockElement(block);
        }
        
        // Remover preview
        preview.remove();
      }
      
      // Cambiar a modo mover
      this.dragState.isCopying = false;
      this.svg.style.cursor = 'move';
    }

    /**
     * Inicia redimensionado
     */
    startResize(handle, point) {
      const blockElement = handle.closest('.time-block');
      const blockId = parseInt(blockElement.getAttribute('data-block-id'));
      const block = this.blocks.find(b => b.id === blockId);
      if (!block) return;
      if (this.isProhibirLocked(block)) return;

      const side = handle.getAttribute('data-side');

      this.resizeState = {
        blockId: blockId,
        side: side,
        startPoint: point,
        originalStartCol: block.startCol,
        originalEndCol: block.endCol
      };

      this.activeBlock = blockId;
    }

    /**
     * Actualiza redimensionado
     */
    updateResize(point) {
      if (!this.resizeState) return;

      const block = this.blocks.find(b => b.id === this.resizeState.blockId);
      if (!block) return;

      const { cellWidth } = this.options;
      const deltaX = point.x - this.resizeState.startPoint.x;
      const colOffset = Math.round(deltaX / cellWidth);

      let newStartCol = this.resizeState.originalStartCol;
      let newEndCol = this.resizeState.originalEndCol;

      if (this.resizeState.side === 'left') {
        newStartCol = this.resizeState.originalStartCol + colOffset;
        newStartCol = Math.max(0, Math.min(newEndCol - 1, newStartCol));
      } else {
        newEndCol = this.resizeState.originalEndCol + colOffset;
        newEndCol = Math.max(newStartCol + 1, Math.min(this.gridDimensions.cols, newEndCol));
      }

      newStartCol = this.snapToGrid(newStartCol);
      newEndCol = this.snapToGrid(newEndCol);

      if (!this.hasCollision(block.dia, newStartCol, newEndCol, this.resizeState.blockId)) {
        block.startCol = newStartCol;
        block.endCol = newEndCol;
        this.updateBlockElement(block);
      }
    }

    /**
     * Finaliza redimensionado
     */
    finishResize() {
      this.resizeState = null;
      this.activeBlock = null;
      this.notifyChange();
      this.renderBlocks();
    }

    /**
     * Aplica snap al grid
     */
    snapToGrid(col) {
      const { minutesPerCell, snapMinutes } = this.options;
      const snapCells = snapMinutes / minutesPerCell;
      return Math.round(col / snapCells) * snapCells;
    }

    /**
     * Detecta colisiones
     */
    hasCollision(day, startCol, endCol, excludeBlockId = null) {
      const { gapMinutes, minutesPerCell } = this.options;
      const gapCells = gapMinutes / minutesPerCell;

      for (const block of this.blocks) {
        if (block.id === excludeBlockId) continue;
        if (block.dia !== day) continue;

        const blockStart = block.startCol - gapCells;
        const blockEnd = block.endCol + gapCells;

        if (startCol < blockEnd && endCol > blockStart) {
          return true;
        }
      }

      return false;
    }

    /**
     * Agrega un bloque
     */
    addBlock(day, startCol, endCol, prohibir) {
      if (this.singleSelect) {
        this.blocks = [];
        this.selectedBlockId = null;
      }

      const block = {
        id: this.nextBlockId++,
        dia: day,
        startCol: startCol,
        endCol: endCol,
        prohibir: prohibir,
        selected: false
      };

      this.blocks.push(block);
      this.renderBlocks();
      this.notifyChange();
    }

    /**
     * Elimina un bloque
     */
    deleteBlock(blockId) {
      this.blocks = this.blocks.filter(b => b.id !== blockId);
      this.selectedBlockId = null;
      this.renderBlocks();
      this.notifyChange();
    }

    /**
     * Renderiza todos los bloques
     */
    renderBlocks() {
      this.blocksGroup.innerHTML = '';

      for (const block of this.blocks) {
        const element = this.createBlockElement(block);
        this.blocksGroup.appendChild(element);
      }
    }

    /**
     * Crea el elemento SVG de un bloque
     */
    createBlockElement(block) {
      const { cellWidth, cellHeight, colors } = this.options;
      const { leftMargin, topMargin, containerWidth } = this.gridDimensions;

      const x = leftMargin + (block.startCol * cellWidth);
      const y = topMargin + (block.dia * cellHeight);
      const width = (block.endCol - block.startCol) * cellWidth;
      const height = cellHeight - 4;

      const color = block.prohibir ? colors.prohibir : colors.selected;
      const disabledColor = block.prohibir ? colors.disabledProhibir : colors.disabledSelected;

      const isSelected = this.selectedBlockId === block.id;

      const group = document.createElementNS('http://www.w3.org/2000/svg', 'g');
      group.classList.add('time-block');
      if (isSelected) {
        group.classList.add('selected');
      }
      group.setAttribute('data-block-id', block.id);
      group.style.pointerEvents = 'all';

      const rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
      rect.setAttribute('x', x);
      rect.setAttribute('y', y + 2);
      rect.setAttribute('width', width);
      rect.setAttribute('height', height);
      rect.setAttribute('fill', this.disabled ? disabledColor : color);
      rect.setAttribute('stroke', isSelected ? colors.selectedBorder : colors.blockBorder);
      rect.setAttribute('stroke-width', isSelected ? '3' : '2');
      rect.setAttribute('rx', '4');
      rect.style.cursor = this.isProhibirLocked(block)
        ? 'not-allowed'
        : (isSelected ? (this.isAltPressed ? 'copy' : 'move') : 'pointer');  // ⭐ NUEVO

      if (isSelected) {
        rect.setAttribute('filter', 'drop-shadow(0 4px 8px rgba(0,0,0,0.3))');
      }

      group.appendChild(rect);

      const isTiny = containerWidth < 400;
      const isSmall = containerWidth < 600;
      const textFontSize = isTiny ? '8' : isSmall ? '9' : '11';

      const showText = width > 30;

      if (showText) {
        const timeText = this.getTimeFromColumn(block.startCol) + ' - ' + this.getTimeFromColumn(block.endCol);
        const text = document.createElementNS('http://www.w3.org/2000/svg', 'text');
        text.setAttribute('x', x + width / 2);
        text.setAttribute('y', y + height / 2 + 4);
        text.setAttribute('text-anchor', 'middle');
        text.setAttribute('font-size', textFontSize);
        text.setAttribute('font-weight', 'bold');
        text.setAttribute('fill', 'white');
        text.style.pointerEvents = 'none';
        text.textContent = timeText;
        group.appendChild(text);
      }

      // Solo mostrar handles y botón si está seleccionado
      if (isSelected) {
        if (!this.isProhibirLocked(block)) {
          this.addResizeHandles(group, x, y, width, height);
          this.addDeleteButton(group, x, y, width);
        }
      }

      return group;
    }

    /**
     * Agrega handles de resize
     */
    addResizeHandles(group, x, y, width, height) {
      const handleWidth = 8;

      const leftHandle = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
      leftHandle.setAttribute('x', x);
      leftHandle.setAttribute('y', y + 2);
      leftHandle.setAttribute('width', handleWidth);
      leftHandle.setAttribute('height', height);
      leftHandle.setAttribute('fill', 'rgba(255,255,255,0.3)');
      leftHandle.setAttribute('data-side', 'left');
      leftHandle.classList.add('resize-handle');
      leftHandle.style.cursor = 'ew-resize';
      leftHandle.style.pointerEvents = 'all';
      group.appendChild(leftHandle);

      const rightHandle = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
      rightHandle.setAttribute('x', x + width - handleWidth);
      rightHandle.setAttribute('y', y + 2);
      rightHandle.setAttribute('width', handleWidth);
      rightHandle.setAttribute('height', height);
      rightHandle.setAttribute('fill', 'rgba(255,255,255,0.3)');
      rightHandle.setAttribute('data-side', 'right');
      rightHandle.classList.add('resize-handle');
      rightHandle.style.cursor = 'ew-resize';
      rightHandle.style.pointerEvents = 'all';
      group.appendChild(rightHandle);
    }

    /**
     * Agrega botón de eliminar
     */
    addDeleteButton(group, x, y, width) {
      const btnSize = 16;
      const btnX = x + width - btnSize - 4;
      const btnY = y + 4;

      const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
      circle.setAttribute('cx', btnX + btnSize / 2);
      circle.setAttribute('cy', btnY + btnSize / 2);
      circle.setAttribute('r', btnSize / 2);
      circle.setAttribute('fill', 'rgba(0,0,0,0.3)');
      circle.classList.add('delete-button');
      circle.style.cursor = 'pointer';
      circle.style.pointerEvents = 'all';
      group.appendChild(circle);

      const xMark = document.createElementNS('http://www.w3.org/2000/svg', 'text');
      xMark.setAttribute('x', btnX + btnSize / 2);
      xMark.setAttribute('y', btnY + btnSize / 2 + 4);
      xMark.setAttribute('text-anchor', 'middle');
      xMark.setAttribute('font-size', '12');
      xMark.setAttribute('font-weight', 'bold');
      xMark.setAttribute('fill', 'white');
      xMark.textContent = '×';
      xMark.style.pointerEvents = 'none';
      group.appendChild(xMark);
    }

    /**
     * Actualiza elemento de bloque
     */
    updateBlockElement(block) {
      const element = this.svg.querySelector(`[data-block-id="${block.id}"]`);
      if (!element) return;

      const { cellWidth, cellHeight } = this.options;
      const { leftMargin, topMargin } = this.gridDimensions;

      const x = leftMargin + (block.startCol * cellWidth);
      const y = topMargin + (block.dia * cellHeight);
      const width = (block.endCol - block.startCol) * cellWidth;
      const height = cellHeight - 4;

      const rect = element.querySelector('rect');
      rect.setAttribute('x', x);
      rect.setAttribute('y', y + 2);
      rect.setAttribute('width', width);

      const text = element.querySelector('text');
      if (text) {
        const timeText = this.getTimeFromColumn(block.startCol) + ' - ' + this.getTimeFromColumn(block.endCol);
        text.textContent = timeText;
        text.setAttribute('x', x + width / 2);
        text.setAttribute('y', y + height / 2 + 4);
      }

      const handles = element.querySelectorAll('.resize-handle');
      if (handles.length > 0) {
        handles[0].setAttribute('x', x);
        handles[0].setAttribute('y', y + 2);
        handles[1].setAttribute('x', x + width - 8);
        handles[1].setAttribute('y', y + 2);
      }

      const deleteBtn = element.querySelector('.delete-button');
      if (deleteBtn) {
        deleteBtn.setAttribute('cx', x + width - 12);
        deleteBtn.setAttribute('cy', y + 6);
        const xMark = element.querySelectorAll('text')[1];
        if (xMark) {
          xMark.setAttribute('x', x + width - 12);
          xMark.setAttribute('y', y + 10);
        }
      }
    }

    /**
     * Actualiza cursor
     */
    updateCursor(target) {
      if (target.classList.contains('resize-handle')) {
        this.svg.style.cursor = 'ew-resize';
      } else if (target.closest('.time-block')) {
        const blockElement = target.closest('.time-block');
        const blockId = blockElement ? parseInt(blockElement.getAttribute('data-block-id')) : null;
        const block = blockId ? this.blocks.find(b => b.id === blockId) : null;

        // Los bloques prohibir no pueden moverse
        if (this.isProhibirLocked(block)) {
          this.svg.style.cursor = 'not-allowed';
        } else if (this.selectedBlockId) {
          // ⭐ NUEVO: Cursor diferente si Alt está presionado
          this.svg.style.cursor = this.isAltPressed ? 'copy' : 'move';
        } else {
          this.svg.style.cursor = 'pointer';
        }
      } else {
        this.svg.style.cursor = 'crosshair';
      }
    }

    /**
     * Obtiene posición del mouse
     */
    getMousePosition(e) {
      const rect = this.svg.getBoundingClientRect();
      return {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      };
    }

    /**
     * Obtiene celda desde punto
     */
    getCellFromPoint(point) {
      const { cellWidth, cellHeight } = this.options;
      const { leftMargin, topMargin, cols, rows } = this.gridDimensions;

      const x = point.x - leftMargin;
      const y = point.y - topMargin;

      if (x < 0 || y < 0) return null;

      const col = Math.floor(x / cellWidth);
      const day = Math.floor(y / cellHeight);

      if (col >= 0 && col < cols && day >= 0 && day < rows) {
        return { day, col };
      }

      return null;
    }

    /**
     * Convierte columna a tiempo
     */
    getTimeFromColumn(col) {
      const totalMinutes = col * this.options.minutesPerCell;
      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    }

    /**
     * Convierte tiempo a columna
     */
    getColumnFromTime(time, roundMode = 'floor') {
      const [hoursStr = '0', minutesStr = '0', secondsStr = '0'] = String(time).split(':');
      const hours = Number(hoursStr) || 0;
      const minutes = Number(minutesStr) || 0;
      const seconds = Number(secondsStr) || 0;
      const totalMinutes = hours * 60 + minutes + (seconds / 60);
      const rawCol = totalMinutes / this.options.minutesPerCell;

      if (roundMode === 'ceil') {
        return Math.ceil(rawCol);
      }

      return Math.floor(rawCol);
    }

    /**
     * Convierte columna a tiempo con segundos
     */
    getTimeWithSeconds(col) {
      const totalMinutes = col * this.options.minutesPerCell;

      // 24:00 no es una hora valida en formato HH:mm:ss
      if (totalMinutes >= 24 * 60) {
        return '23:59:59';
      }

      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;

      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:00`;
    }

    setDays(days) {
      this.options.days = days;
      this.days = this.options.days;
      
      this.renderBlocks();
      this.notifyChange();
    }

    getDays() {
      return this.days;
    }

    /**
     * Obtiene rangos en formato JSON
     */
    getRanges() {
      return this.blocks.map(block => ({
        dia: this.days[block.dia],
        timeInit: this.getTimeFromColumn(block.startCol),
        timeEnd: this.getTimeWithSeconds(block.endCol),
        prohibir: block.prohibir
      }));
    }

    /**
     * Establece rangos desde JSON
     */
    setRanges(ranges) {
      this.blocks = [];
      this.nextBlockId = 1;
      this.selectedBlockId = null;

      ranges.forEach(range => {
        const dayIndex = this.days.indexOf(range.dia);
        if (dayIndex === -1) return;

        const timeEnd = String(range.timeEnd || '00:00');
        const endParts = timeEnd.split(':');
        const endMinutes = Number(endParts[1] || 0);
        const endSeconds = Number(endParts[2] || 0);

        // Para fin de rango: si viene con segundos o fuera del paso del grid,
        // redondeamos hacia arriba para no perder 1 celda al recargar.
        const endRoundMode = (endSeconds > 0 || (endMinutes % this.options.minutesPerCell !== 0))
          ? 'ceil'
          : 'floor';

        const startCol = this.getColumnFromTime(range.timeInit, 'floor');
        const endCol = this.getColumnFromTime(timeEnd, endRoundMode);

        this.blocks.push({
          id: this.nextBlockId++,
          dia: dayIndex,
          startCol: startCol,
          endCol: endCol,
          prohibir: range.prohibir,
          selected: false
        });
      });

      this.renderBlocks();
      this.notifyChange();
    }

    /**
     * Establece modo (normal/prohibir)
     */
    setMode(mode) {
      if (mode !== 'normal' && mode !== 'prohibir') return;
      this.currentMode = mode;
    }

    /**
     * Obtiene modo actual
     */
    getMode() {
      return this.currentMode;
    }

    /**
     * Establece gap
     */
    setGap(gapMinutes) {
      this.options.gapMinutes = gapMinutes;
    }

    /**
     * Limpia selección
     */
    clearSelection() {
      this.blocks = [];
      this.nextBlockId = 1;
      this.selectedBlockId = null;
      this.renderBlocks();
      this.notifyChange();
    }

    /**
     * Notifica cambios
     */
    notifyChange() {
      if (typeof this.options.onChange === 'function') {
        this.options.onChange(this.getRanges());
      }
    }

    /**
     * Convierte a JSON
     */
    toJSON() {
      return JSON.stringify(this.getRanges(), null, 2);
    }

    /**
     * Destruye el selector
     */
    destroy() {
      if (this.svg) this.svg.remove();
      this.blocks = [];

      if (this.options.responsive && this.handleResize) {
        window.removeEventListener('resize', this.handleResize);
      }

      // ⭐ NUEVO: Remover eventos de teclado
      if (this.handleKeyDown) {
        window.removeEventListener('keydown', this.handleKeyDown);
      }
      if (this.handleKeyUp) {
        window.removeEventListener('keyup', this.handleKeyUp);
      }
    }

    /**
     * Debounce utility
     */
    debounce(func, wait) {
      let timeout;
      return function executedFunction(...args) {
        const later = () => {
          clearTimeout(timeout);
          func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
      };
    }

    /**
     * Recrea el grid
     */
    recreateGrid() {
      const currentBlocks = [...this.blocks];

      this.svg.innerHTML = '';

      this.blocksGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
      this.blocksGroup.setAttribute('id', 'blocks-group');
      this.svg.appendChild(this.blocksGroup);

      const { minutesPerCell, startHour, endHour } = this.options;
      const totalMinutes = (endHour - startHour) * 60;
      const cols = totalMinutes / minutesPerCell;
      const rows = this.days.length;

      const containerWidth = this.container.clientWidth || 1200;

      let leftMargin = 50;
      let topMargin = 30;
      let cellHeight = this.options.cellHeight;

      const width = leftMargin + (cols * this.options.cellWidth);
      const height = topMargin + (rows * cellHeight);

      this.svg.setAttribute('viewBox', `${this.minX} ${this.minY} ${width} ${height}`);
      this.svg.setAttribute('height', height);
      this.gridDimensions = { leftMargin, topMargin, cols, rows, width, height, containerWidth };

      this.createGrid();

      this.blocks = currentBlocks;
      this.renderBlocks();
    }

    /**
     * Activa/desactiva responsive
     */
    setResponsive(enabled) {
      if (this.options.responsive === enabled) return;

      this.options.responsive = enabled;

      if (enabled) {
        this.handleResize = this.debounce(() => {
          this.recreateGrid();
        }, 250);
        window.addEventListener('resize', this.handleResize);
        this.recreateGrid();
      } else {
        if (this.handleResize) {
          window.removeEventListener('resize', this.handleResize);
        }
        this.options.cellWidth = 8;
        this.recreateGrid();
      }
    }
  }

  // Exponer globalmente
  window.TimeRangeSelector = TimeRangeSelector;

})(window);